import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { MoreHorizontal, UserMinus, UserCheck } from 'lucide-react';

interface UserData {
  user_id: string;
  email: string;
  full_name: string | null;
  role: string;
  is_active: boolean;
}

interface ClientUserActionsDropdownProps {
  user: UserData;
  onRefresh: () => void;
}

export function ClientUserActionsDropdown({ user, onRefresh }: ClientUserActionsDropdownProps) {
  const [confirmDeactivate, setConfirmDeactivate] = useState(false);
  const [loading, setLoading] = useState(false);

  // Client admins cannot modify other client_admins or admins
  const canModify = user.role !== 'client_admin' && user.role !== 'admin';

  const handleToggleActive = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('client-update-user', {
        body: {
          user_id: user.user_id,
          is_active: !user.is_active,
        },
      });

      if (error) throw error;
      if (!data.success) throw new Error(data.error);

      toast.success(user.is_active ? 'User deactivated' : 'User activated');
      onRefresh();
    } catch (error: any) {
      console.error('Error toggling user status:', error);
      toast.error(error.message || 'Failed to update user');
    } finally {
      setLoading(false);
      setConfirmDeactivate(false);
    }
  };

  if (!canModify) {
    return null;
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="h-8 w-8">
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem 
            onClick={() => setConfirmDeactivate(true)}
            disabled={loading}
          >
            {user.is_active ? (
              <>
                <UserMinus className="h-4 w-4 mr-2" />
                Deactivate
              </>
            ) : (
              <>
                <UserCheck className="h-4 w-4 mr-2" />
                Activate
              </>
            )}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Deactivate/Activate Confirmation */}
      <AlertDialog open={confirmDeactivate} onOpenChange={setConfirmDeactivate}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {user.is_active ? 'Deactivate User?' : 'Activate User?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {user.is_active 
                ? `This will prevent ${user.full_name || user.email} from accessing the platform. They can be reactivated later.`
                : `This will restore ${user.full_name || user.email}'s access to the platform.`
              }
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleToggleActive} disabled={loading}>
              {loading ? 'Processing...' : (user.is_active ? 'Deactivate' : 'Activate')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
